const MAX_USER_ATTRIBUTE_QUERY_TAGS = 20;
const MAX_QUERY_TAG_KEY_LENGTH = 60;
const MAX_QUERY_TAG_VALUE_LENGTH = 60;
const USER_ATTRIBUTE_QUERY_TAG_PREFIX = 'user_attribute_';
const sanitizeQueryTagString = (value, fallback, maxLength) => value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '_')
    .substring(0, maxLength) || fallback;
export const sanitizeQueryTagKey = (key) => sanitizeQueryTagString(key, 'empty_key', MAX_QUERY_TAG_KEY_LENGTH);
export const sanitizeQueryTagValue = (value) => sanitizeQueryTagString(value, 'empty_value', MAX_QUERY_TAG_VALUE_LENGTH);
export const getUserAttributeQueryTags = (userAttributes) => Object.fromEntries(Object.entries(userAttributes).reduce((acc, [name, values]) => {
    if (acc.length >= MAX_USER_ATTRIBUTE_QUERY_TAGS) {
        return acc;
    }
    const tagName = sanitizeQueryTagKey(`${USER_ATTRIBUTE_QUERY_TAG_PREFIX}${name}`);
    const value = values
        .map((attribute) => attribute.trim())
        .filter(Boolean)
        .join(',')
        .trim();
    if (value) {
        acc.push([
            tagName,
            sanitizeQueryTagValue(value),
        ]);
    }
    return acc;
}, []));
/**
 * Sparse sidecar of timestamp domains, keyed like the catalog itself. It
 * lives on the catalog under a reserved key next to the database keys, so it
 * survives the `cached_warehouse` JSON round-trip unchanged and is inert to
 * readers that only look up their own database names.
 */
export const WAREHOUSE_TIMESTAMP_DOMAINS_KEY = '__lightdashTimestampDomains';
export const getCatalogTimestampDomain = (catalog, database, schema, table, column) => catalog[WAREHOUSE_TIMESTAMP_DOMAINS_KEY]?.[database]?.[schema]?.[table]?.[column];
export const setCatalogTimestampDomain = (catalog, database, schema, table, column, timestampDomain) => {
    if (timestampDomain === undefined)
        return;
    const catalogWithDomains = catalog;
    const domains = catalogWithDomains[WAREHOUSE_TIMESTAMP_DOMAINS_KEY] ?? {};
    catalogWithDomains[WAREHOUSE_TIMESTAMP_DOMAINS_KEY] = domains;
    domains[database] = domains[database] ?? {};
    domains[database][schema] = domains[database][schema] ?? {};
    domains[database][schema][table] = domains[database][schema][table] ?? {};
    domains[database][schema][table][column] = timestampDomain;
};
/**
 * True when the catalog was produced by domain-aware code: the sidecar key is
 * present, even if empty. A missing key means a pre-domain cache that should
 * be refetched once so timestamp columns get classified.
 */
export const catalogHasTimestampDomains = (catalog) => WAREHOUSE_TIMESTAMP_DOMAINS_KEY in catalog;
/**
 * Stamps the (possibly empty) sidecar onto a freshly fetched catalog so
 * `catalogHasTimestampDomains` can tell it apart from a pre-domain cache —
 * clients only create the key when they classify at least one column.
 */
export const ensureCatalogTimestampDomainsKey = (catalog) => {
    const catalogWithDomains = catalog;
    catalogWithDomains[WAREHOUSE_TIMESTAMP_DOMAINS_KEY] =
        catalogWithDomains[WAREHOUSE_TIMESTAMP_DOMAINS_KEY] ?? {};
};
const isPlainRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
export const isWarehouseNestedColumnShape = (value) => isPlainRecord(value) &&
    typeof value.repeated === 'boolean' &&
    typeof value.record === 'boolean';
/**
 * Sidecar of nested column shapes keyed database → schema → table → column
 * path, stored under a reserved key next to the database keys like the
 * timestamp domains. Its value shares the catalog's key space, so it is read
 * back through runtime checks rather than a cast; a malformed sidecar from an
 * older cache is ignored.
 */
export const WAREHOUSE_NESTED_COLUMNS_KEY = '__lightdashNestedColumns';
const getNestedColumnsSidecar = (catalog) => {
    const sidecar = catalog[WAREHOUSE_NESTED_COLUMNS_KEY];
    return isPlainRecord(sidecar) ? sidecar : undefined;
};
export const getCatalogNestedColumnShape = (catalog, database, schema, table, columnPath) => {
    const shape = [database, schema, table, columnPath].reduce((node, key) => (isPlainRecord(node) ? node[key] : undefined), getNestedColumnsSidecar(catalog));
    return isWarehouseNestedColumnShape(shape) ? shape : undefined;
};
const ensureRecord = (parent, key) => {
    const existing = parent[key];
    if (isPlainRecord(existing))
        return existing;
    const created = {};
    // eslint-disable-next-line no-param-reassign
    parent[key] = created;
    return created;
};
const getSidecar = (catalog, key) => {
    const existing = catalog[key];
    if (isPlainRecord(existing))
        return existing;
    const created = {};
    Object.assign(catalog, { [key]: created });
    return created;
};
export const setCatalogNestedColumnShape = (catalog, database, schema, table, columnPath, shape) => {
    if (shape === undefined)
        return;
    const sidecar = getSidecar(catalog, WAREHOUSE_NESTED_COLUMNS_KEY);
    ensureRecord(ensureRecord(ensureRecord(sidecar, database), schema), table)[columnPath] = shape;
};
// Native types `DimensionType` collapses; see packages/warehouses/src/warehouseClients/AGENTS.md
export const WAREHOUSE_NATIVE_TYPES_KEY = '__lightdashNativeTypes';
export const getCatalogTableNativeTypes = (catalog, database, schema, table) => {
    const columns = [database, schema, table].reduce((node, key) => (isPlainRecord(node) ? node[key] : undefined), catalog[WAREHOUSE_NATIVE_TYPES_KEY]);
    if (!isPlainRecord(columns))
        return {};
    return Object.fromEntries(Object.entries(columns).filter((entry) => typeof entry[1] === 'string'));
};
export const setCatalogNativeType = (catalog, database, schema, table, column, nativeType) => {
    const sidecar = getSidecar(catalog, WAREHOUSE_NATIVE_TYPES_KEY);
    ensureRecord(ensureRecord(ensureRecord(sidecar, database), schema), table)[column] = nativeType;
};
/**
 * Tables whose nested column shapes the warehouse would not report, keyed
 * database → schema → table → reason. Tells the compiler apart a table with
 * no nested columns from one it should warn about.
 */
export const WAREHOUSE_NESTED_COLUMNS_UNAVAILABLE_KEY = '__lightdashNestedColumnsUnavailable';
export const getCatalogNestedColumnsUnavailableReason = (catalog, database, schema, table) => {
    const reason = [database, schema, table].reduce((node, key) => (isPlainRecord(node) ? node[key] : undefined), catalog[WAREHOUSE_NESTED_COLUMNS_UNAVAILABLE_KEY]);
    return typeof reason === 'string' ? reason : undefined;
};
export const setCatalogNestedColumnsUnavailable = (catalog, database, schema, table, reason) => {
    const sidecar = getSidecar(catalog, WAREHOUSE_NESTED_COLUMNS_UNAVAILABLE_KEY);
    ensureRecord(ensureRecord(sidecar, database), schema)[table] = reason;
};
/**
 * What the compile requested but the warehouse did not return at the last
 * full catalog fetch, keyed database → schema → table → `true` for a missing
 * table or the list of missing column names for a table that exists. Lets a
 * later compile tell an entry it already knows is absent from a new one.
 */
export const WAREHOUSE_MISSING_ENTRIES_KEY = '__lightdashMissingEntries';
const toMissingEntryColumns = (value) => {
    if (value === true)
        return null;
    if (Array.isArray(value) &&
        value.length > 0 &&
        value.every((column) => typeof column === 'string')) {
        return value;
    }
    return undefined;
};
export const getCatalogMissingEntries = (catalog) => {
    const sidecar = catalog[WAREHOUSE_MISSING_ENTRIES_KEY];
    if (!isPlainRecord(sidecar))
        return [];
    return Object.entries(sidecar).flatMap(([database, schemas]) => isPlainRecord(schemas)
        ? Object.entries(schemas).flatMap(([schema, tables]) => isPlainRecord(tables)
            ? Object.entries(tables).flatMap(([table, value]) => {
                const columns = toMissingEntryColumns(value);
                return columns === undefined
                    ? []
                    : [{ database, schema, table, columns }];
            })
            : [])
        : []);
};
export const setCatalogMissingEntries = (catalog, entries) => {
    const sidecar = {};
    entries.forEach(({ database, schema, table, columns }) => {
        ensureRecord(ensureRecord(sidecar, database), schema)[table] =
            columns ?? true;
    });
    Object.assign(catalog, { [WAREHOUSE_MISSING_ENTRIES_KEY]: sidecar });
};
export var WarehouseTableType;
(function (WarehouseTableType) {
    WarehouseTableType["TABLE"] = "table";
    WarehouseTableType["VIEW"] = "view";
    WarehouseTableType["MATERIALIZED_VIEW"] = "materialized_view";
    WarehouseTableType["EXTERNAL"] = "external";
})(WarehouseTableType || (WarehouseTableType = {}));
export const isWarehouseTableType = (value) => typeof value === 'string' &&
    Object.values(WarehouseTableType).includes(value);
// Maps the table_type strings and engine names warehouses report onto one vocabulary
export const getWarehouseTableType = (rawType) => {
    const normalized = typeof rawType === 'string'
        ? rawType
            .trim()
            .toUpperCase()
            .replace(/[\s_]+/g, ' ')
        : '';
    switch (normalized) {
        case 'VIEW':
        case 'VIRTUAL VIEW':
            return WarehouseTableType.VIEW;
        case 'MATERIALIZED VIEW':
        case 'MATERIALIZEDVIEW':
            return WarehouseTableType.MATERIALIZED_VIEW;
        case 'EXTERNAL':
        case 'EXTERNAL TABLE':
        case 'FOREIGN':
        case 'FOREIGN TABLE':
            return WarehouseTableType.EXTERNAL;
        default:
            return WarehouseTableType.TABLE;
    }
};
export const WAREHOUSE_LISTED_DATABASES_LIMIT = 100;
export var TimeIntervalUnit;
(function (TimeIntervalUnit) {
    TimeIntervalUnit["SECOND"] = "SECOND";
    TimeIntervalUnit["MINUTE"] = "MINUTE";
    TimeIntervalUnit["HOUR"] = "HOUR";
    TimeIntervalUnit["DAY"] = "DAY";
    TimeIntervalUnit["WEEK"] = "WEEK";
    TimeIntervalUnit["MONTH"] = "MONTH";
    TimeIntervalUnit["YEAR"] = "YEAR";
})(TimeIntervalUnit || (TimeIntervalUnit = {}));
export var PartitionType;
(function (PartitionType) {
    PartitionType["DATE"] = "DATE";
    PartitionType["RANGE"] = "RANGE";
})(PartitionType || (PartitionType = {}));
export var PartitionGranularity;
(function (PartitionGranularity) {
    PartitionGranularity["HOUR"] = "HOUR";
    PartitionGranularity["DAY"] = "DAY";
    PartitionGranularity["MONTH"] = "MONTH";
    PartitionGranularity["YEAR"] = "YEAR";
})(PartitionGranularity || (PartitionGranularity = {}));
//# sourceMappingURL=warehouse.js.map